import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import { 
  Play, 
  Calendar, 
  Truck, 
  Clock, 
  DollarSign,
  AlertTriangle,
  Loader2,
  History,
  Trash2,
  Target,
  ChevronDown,
  ChevronUp,
  Route,
  X,
} from 'lucide-react';
import { 
  getSimulationTasks, 
  runSimulation, 
  getSimulationHistory,
  deleteSimulation,
  deleteAllSimulations,
  type SimulationConfig,
  type Simulation as SimulationType,
  type SimulationTask,
} from '../services/simulation.api';
import { getZones } from '../services/zones.api';
import { getBaghdadToday } from '../utils/date';
import { useCan } from '../contexts/useAuth';

mapboxgl.accessToken = import.meta.env.VITE_MAPBOX_TOKEN || '';

const BAGHDAD_WAREHOUSE = { lat: 33.284412, lng: 44.448563 };

const ROUTE_COLORS = [
  '#8B5CF6', '#3B82F6', '#10B981', '#F59E0B', '#EF4444',
  '#EC4899', '#06B6D4', '#84CC16', '#F97316', '#6366F1',
  '#14B8A6', '#A855F7', '#F43F5E', '#0EA5E9', '#22C55E',
];

const DEFAULT_CONFIG: SimulationConfig = {
  vehicleMode: 'auto',
  vehicleCount: 10,
  vehicleCapacity: 300,
  minStopsPerRoute: 5, // Not used but kept for interface compatibility
  speedFactor: 1.0,
  maxTravelTimeSeconds: 28800,
  startTime: '15:00',
  endTime: '23:00',
  breaksEnabled: false,
  breakDurationMinutes: 0,
  optimizationGoal: 'balanced',
  serviceTimePerParcel: 5,
  baseServiceTimeMinutes: 5,
  useMerchantTimeWindows: false,
  excludeDedicatedDriverMerchants: true, // Exclude large merchants that need dedicated drivers
  hubStart: true, // Always start from hub
  costPerHour: 4,
  costPerKm: 0,
  fixedVehicleCost: 0,
};

export default function Simulation() {
  const can = useCan();
  // Map refs
  const mapContainer = useRef<HTMLDivElement>(null);
  const map = useRef<mapboxgl.Map | null>(null);
  const markersRef = useRef<mapboxgl.Marker[]>([]);
  const [mapLoaded, setMapLoaded] = useState(false);

  // State
  const [selectedDate, setSelectedDate] = useState<string>(getBaghdadToday());
  const [zoneFilter, setZoneFilter] = useState<string[]>([]); // Zone names for filtering (like Tasks page)
  const [allTasks, setAllTasks] = useState<SimulationTask[]>([]); // All tasks from API
  const [isLoadingTasks, setIsLoadingTasks] = useState(false);
  const [config, setConfig] = useState<SimulationConfig>(DEFAULT_CONFIG);
  const [isRunning, setIsRunning] = useState(false);
  const [currentSimulation, setCurrentSimulation] = useState<SimulationType | null>(null);
  const [history, setHistory] = useState<SimulationType[]>([]);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [zoneDropdownOpen, setZoneDropdownOpen] = useState(false);
  const zoneDropdownRef = useRef<HTMLDivElement>(null);
  const [showHistoryPanel, setShowHistoryPanel] = useState(false);
  const [showRoutesPanel, setShowRoutesPanel] = useState(false);
  const [selectedRouteIndex, setSelectedRouteIndex] = useState<number | null>(null);

  // Collapsible sections
  const [expandedSections, setExpandedSections] = useState({
    dateZone: true,
    vehicle: true,
    time: false,
    optimization: false,
    cost: false,
  });

  const toggleSection = (section: keyof typeof expandedSections) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  // Load tasks when date changes (zone filtering is done on frontend)
  useEffect(() => {
    const loadTasks = async () => {
      setIsLoadingTasks(true);
      setError(null);
      try {
        const { tasks: taskList } = await getSimulationTasks(selectedDate);
        setAllTasks(taskList);
      } catch (err) {
        console.error('Failed to load tasks:', err);
        setError('Failed to load tasks');
        setAllTasks([]);
      } finally {
        setIsLoadingTasks(false);
      }
    };
    loadTasks();
  }, [selectedDate]);

  // Close zone dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (zoneDropdownRef.current && !zoneDropdownRef.current.contains(event.target as Node)) {
        setZoneDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Fetch zones from API for filter dropdown
  const [availableZones, setAvailableZones] = useState<string[]>([]);
  useEffect(() => {
    async function fetchZones() {
      try {
        const zones = await getZones(1); // cityId = 1 (Baghdad)
        setAvailableZones(zones.map(z => z.name).sort());
      } catch (error) {
        console.error('Failed to fetch zones:', error);
        // Fallback to zones from tasks
        const zoneNames = new Set<string>();
        allTasks.forEach((task) => {
          if (task.zone_name) {
            zoneNames.add(task.zone_name);
          }
        });
        setAvailableZones(Array.from(zoneNames).sort());
      }
    }
    fetchZones();
  }, [allTasks]);

  // Filter tasks by zone (like Tasks page)
  const tasks = useMemo(() => {
    if (zoneFilter.length === 0) return allTasks;
    
    return allTasks.filter((task) => {
      if (zoneFilter.includes('no_zone')) {
        return !task.zone_name || zoneFilter.includes(task.zone_name || '');
      }
      return task.zone_name && zoneFilter.includes(task.zone_name);
    });
  }, [allTasks, zoneFilter]);

  // Count RTOs and Pickups
  const rtoCount = useMemo(() => tasks.filter(t => t.type === 'rto').length, [tasks]);
  const pickupCount = useMemo(() => tasks.filter(t => t.type === 'pickup').length, [tasks]);

  // Load history
  const loadHistory = useCallback(async () => {
    setIsLoadingHistory(true);
    try {
      const { simulations } = await getSimulationHistory({ limit: 10 });
      setHistory(simulations);
    } catch (err) {
      console.error('Failed to load history:', err);
    } finally {
      setIsLoadingHistory(false);
    }
  }, []);


  // Initialize map
  useEffect(() => {
    if (!mapContainer.current || map.current) return;

    // Small delay to ensure container has rendered with dimensions
    const timer = setTimeout(() => {
      if (!mapContainer.current) return;

      map.current = new mapboxgl.Map({
        container: mapContainer.current,
        style: 'mapbox://styles/mapbox/light-v11',
        center: [BAGHDAD_WAREHOUSE.lng, BAGHDAD_WAREHOUSE.lat],
        zoom: 11,
      });

      map.current.addControl(new mapboxgl.NavigationControl(), 'top-right');
      map.current.on('load', () => {
        setMapLoaded(true);
        // Trigger resize to ensure map fills container
        map.current?.resize();
      });
    }, 100);

    return () => {
      clearTimeout(timer);
      map.current?.remove();
      map.current = null;
    };
  }, []);

  // Draw tasks on map
  useEffect(() => {
    if (!map.current || !mapLoaded) return;

    // Clear existing markers
    markersRef.current.forEach(marker => marker.remove());
    markersRef.current = [];

    // Add warehouse marker
    const warehouseEl = document.createElement('div');
    warehouseEl.innerHTML = `
      <div style="width: 36px; height: 36px; background: #7C3AED; border: 3px solid white; border-radius: 50%; display: flex; align-items: center; justify-content: center; box-shadow: 0 2px 8px rgba(0,0,0,0.3);">
        <svg width="18" height="18" viewBox="0 0 24 24" fill="white"><path d="M4 21V9L12 3L20 9V21H14V14H10V21H4Z"/></svg>
      </div>
    `;
    const warehouseMarker = new mapboxgl.Marker({ element: warehouseEl })
      .setLngLat([BAGHDAD_WAREHOUSE.lng, BAGHDAD_WAREHOUSE.lat])
      .setPopup(new mapboxgl.Popup({ offset: 25 }).setHTML('<strong>Baghdad Sorting Center</strong>'))
      .addTo(map.current);
    markersRef.current.push(warehouseMarker);

    // Add task markers (only if no simulation result)
    if (!currentSimulation?.result && tasks.length > 0) {
      tasks.forEach((task) => {
        const hasTimeWindow = task.has_time_window;
        const timeWindowText = task.time_window 
          ? `${task.time_window.start} - ${task.time_window.end}` 
          : '';
        
        const parcelCount = task.parcel_count || 1;
        const isRTO = task.type === 'rto';
        const isCash = task.type === 'cash';
        // Pickup: gray (#6B7280), RTO: red (#DC2626), Cash: amber (#D97706)
        const markerColor = isRTO ? '#DC2626' : isCash ? '#D97706' : '#6B7280';
        const typeLabel = isRTO ? 'RTO' : isCash ? 'Cash' : 'Pickup';
        
        const el = document.createElement('div');
        el.innerHTML = `
          <div style="
            min-width: 24px; height: 24px; 
            padding: 0 6px;
            background: ${markerColor}; 
            border: 2px solid ${hasTimeWindow ? '#8B5CF6' : 'white'}; 
            border-radius: 12px; 
            box-shadow: 0 1px 3px rgba(0,0,0,0.3);
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 11px;
            font-weight: bold;
            color: white;
            cursor: pointer;
          ">${parcelCount}</div>
        `;
        const marker = new mapboxgl.Marker({ element: el })
          .setLngLat([task.lng, task.lat])
          .setPopup(new mapboxgl.Popup({ offset: 15 }).setHTML(`
            <div style="font-size: 12px; min-width: 150px;">
              <div style="display: flex; align-items: center; gap: 6px; margin-bottom: 4px;">
                <span style="
                  display: inline-block;
                  padding: 2px 6px;
                  border-radius: 4px;
                  font-size: 10px;
                  font-weight: bold;
                  background: ${isRTO ? '#FEE2E2' : isCash ? '#FEF3C7' : '#DBEAFE'};
                  color: ${isRTO ? '#DC2626' : isCash ? '#D97706' : '#2563EB'};
                ">${typeLabel}</span>
                <strong>${task.merchant_name || 'Unknown'}</strong>
              </div>
              <span style="color: #666;">${parcelCount} orders</span>
              ${hasTimeWindow ? `
                <div style="margin-top: 8px; padding: 6px 8px; background: #F3E8FF; border-radius: 4px; font-size: 11px; color: #7C3AED;">
                  <strong>⏰ Preferred:</strong> ${timeWindowText}
                </div>
              ` : ''}
            </div>
          `))
          .addTo(map.current!);
        markersRef.current.push(marker);
      });

      // Fit bounds to tasks
      if (tasks.length > 0) {
        const bounds = new mapboxgl.LngLatBounds();
        bounds.extend([BAGHDAD_WAREHOUSE.lng, BAGHDAD_WAREHOUSE.lat]);
        tasks.forEach(t => bounds.extend([t.lng, t.lat]));
        map.current.fitBounds(bounds, { padding: 50 });
      }
    }
  }, [mapLoaded, tasks, currentSimulation]);


  // Draw simulation routes on map (like RoutePreview)
  useEffect(() => {
    if (!map.current || !mapLoaded || !currentSimulation?.versoResponse) return;

    // Clear existing layers and markers
    const existingLayers = map.current.getStyle()?.layers || [];
    existingLayers.forEach(layer => {
      if (layer.id.startsWith('route-')) {
        map.current?.removeLayer(layer.id);
      }
    });
    const existingSources = Object.keys(map.current.getStyle()?.sources || {});
    existingSources.forEach(source => {
      if (source.startsWith('route-')) {
        map.current?.removeSource(source);
      }
    });

    markersRef.current.forEach(marker => marker.remove());
    markersRef.current = [];

    // Add warehouse marker
    const warehouseEl = document.createElement('div');
    warehouseEl.innerHTML = `
      <div style="width: 40px; height: 40px; background: #7C3AED; border: 3px solid white; border-radius: 50%; display: flex; align-items: center; justify-content: center; box-shadow: 0 2px 8px rgba(0,0,0,0.3); cursor: pointer;">
        <svg width="20" height="20" viewBox="0 0 24 24" fill="white"><path d="M4 21V9L12 3L20 9V21H14V14H10V21H4Z"/></svg>
      </div>
    `;
    const hubDescription = config.hubStart ? 'Start & End Location' : 'End Location Only';
    const warehouseMarker = new mapboxgl.Marker({ element: warehouseEl })
      .setLngLat([BAGHDAD_WAREHOUSE.lng, BAGHDAD_WAREHOUSE.lat])
      .setPopup(new mapboxgl.Popup({ offset: 25 }).setHTML(`
        <div style="padding: 8px;"><strong>Baghdad Sorting Center</strong><br/><span style="color: #666; font-size: 12px;">${hubDescription}</span></div>
      `))
      .addTo(map.current);
    markersRef.current.push(warehouseMarker);

    const bounds = new mapboxgl.LngLatBounds();
    bounds.extend([BAGHDAD_WAREHOUSE.lng, BAGHDAD_WAREHOUSE.lat]);

    // Build GLOBAL location groups across ALL routes to handle overlapping markers
    // This ensures markers from different routes at the same location are also offset
    const globalLocationGroups = new Map<string, { routeIndex: number, stopIndex: number, stop: typeof currentSimulation.versoResponse.routes[0]['stops'][0] }[]>();
    currentSimulation.versoResponse.routes.forEach((route, routeIndex) => {
      route.stops.forEach((stop, stopIndex) => {
        const key = `${stop.location.lat.toFixed(5)},${stop.location.lng.toFixed(5)}`;
        if (!globalLocationGroups.has(key)) {
          globalLocationGroups.set(key, []);
        }
        globalLocationGroups.get(key)!.push({ routeIndex, stopIndex, stop });
      });
    });

    // Draw each route with straight lines and numbered stop markers
    currentSimulation.versoResponse.routes.forEach((route, routeIndex) => {
      const color = ROUTE_COLORS[routeIndex % ROUTE_COLORS.length];

      // Draw route line (straight lines between stops)
      // If hubStart is enabled, start from warehouse; always end at warehouse
      const coordinates: [number, number][] = [
        ...(config.hubStart ? [[BAGHDAD_WAREHOUSE.lng, BAGHDAD_WAREHOUSE.lat] as [number, number]] : []),
        ...route.stops.map(stop => [stop.location.lng, stop.location.lat] as [number, number]),
        [BAGHDAD_WAREHOUSE.lng, BAGHDAD_WAREHOUSE.lat],
      ];

        const sourceId = `route-line-${routeIndex}`;
        const layerId = `route-layer-${routeIndex}`;

        if (!map.current!.getSource(sourceId)) {
          map.current!.addSource(sourceId, {
            type: 'geojson',
            data: {
              type: 'Feature',
              properties: {},
              geometry: { type: 'LineString', coordinates },
            },
          });

          map.current!.addLayer({
            id: layerId,
            type: 'line',
            source: sourceId,
            layout: { 'line-join': 'round', 'line-cap': 'round' },
            paint: { 'line-color': color, 'line-width': 3, 'line-opacity': 0.8 },
          });
        }

        // Add numbered stop markers
        route.stops.forEach((stop, stopIndex) => {
          // Find task details from tasks array
          const taskDetails = tasks.find(t => t.task_uid === stop.taskUid);
          const merchantName = taskDetails?.merchant_name || 'Unknown';
          const parcelCount = taskDetails?.parcel_count || 0;
          const isRTO = taskDetails?.type === 'rto';
          const isCash = taskDetails?.type === 'cash';
          const arrivalTime = new Date(stop.arrivalTimestamp * 1000).toLocaleTimeString('en-GB', {
            hour: '2-digit', minute: '2-digit', timeZone: 'Asia/Baghdad'
          });

          // Check if this stop overlaps with others at same location (across ALL routes)
          const locationKey = `${stop.location.lat.toFixed(5)},${stop.location.lng.toFixed(5)}`;
          const overlappingStops = globalLocationGroups.get(locationKey) || [];
          const overlapIndex = overlappingStops.findIndex(s => s.routeIndex === routeIndex && s.stopIndex === stopIndex);
          const hasOverlap = overlappingStops.length > 1;

          // Offset overlapping markers - larger offset so they're visible when zoomed out
          // 0.0005 degrees ≈ 50 meters, visible at zoom level 14+
          const offsetAngle = hasOverlap ? (overlapIndex * (360 / overlappingStops.length)) * (Math.PI / 180) : 0;
          const offsetDistance = hasOverlap ? 0.0005 : 0; // ~50 meters offset
          const offsetLng = stop.location.lng + Math.cos(offsetAngle) * offsetDistance;
          const offsetLat = stop.location.lat + Math.sin(offsetAngle) * offsetDistance;

          // All markers use route color with sequence number, RTO/Cash has indicator badge
          const taskTypeBadge = isRTO
            ? '<span style="background: #F59E0B; color: white; padding: 2px 6px; border-radius: 4px; font-size: 10px; font-weight: 600;">RTO</span>'
            : isCash
            ? '<span style="background: #D97706; color: white; padding: 2px 6px; border-radius: 4px; font-size: 10px; font-weight: 600;">Cash</span>'
            : '<span style="background: #3B82F6; color: white; padding: 2px 6px; border-radius: 4px; font-size: 10px; font-weight: 600;">Pickup</span>';

          const el = document.createElement('div');
          el.innerHTML = `
            <div style="position: relative;">
              <div style="
                width: 28px; height: 28px;
                background: ${color};
                border: 3px solid white;
                border-radius: 50%;
                display: flex; align-items: center; justify-content: center;
                color: white; font-weight: bold; font-size: 12px;
                box-shadow: 0 2px 6px rgba(0,0,0,0.3);
                cursor: pointer;
                transition: transform 0.2s ease;
              "
              onmouseover="this.style.transform='scale(1.3)'; this.style.zIndex='1000';"
              onmouseout="this.style.transform='scale(1)'; this.style.zIndex='auto';"
              >${stopIndex + 1}</div>
              ${isRTO ? `
                <div style="
                  position: absolute;
                  top: -4px;
                  right: -4px;
                  width: 14px;
                  height: 14px;
                  background: #F59E0B;
                  border: 1.5px solid white;
                  border-radius: 50%;
                  display: flex;
                  align-items: center;
                  justify-content: center;
                  box-shadow: 0 1px 3px rgba(0,0,0,0.3);
                ">
                  <svg width="8" height="8" viewBox="0 0 24 24" fill="white">
                    <path d="M19 7v4H5.83l3.58-3.59L8 6l-6 6 6 6 1.41-1.41L5.83 13H21V7h-2z"/>
                  </svg>
                </div>
              ` : isCash ? `
                <div style="
                  position: absolute;
                  top: -4px;
                  right: -4px;
                  width: 14px;
                  height: 14px;
                  background: #D97706;
                  border: 1.5px solid white;
                  border-radius: 50%;
                  display: flex;
                  align-items: center;
                  justify-content: center;
                  box-shadow: 0 1px 3px rgba(0,0,0,0.3);
                  font-size: 8px;
                  color: white;
                  font-weight: bold;
                ">$</div>
              ` : ''}
            </div>
          `;

          const popup = new mapboxgl.Popup({ offset: 25, closeButton: false }).setHTML(`
            <div style="padding: 12px; min-width: 180px;">
              <div style="display: flex; align-items: center; gap: 8px; margin-bottom: 8px;">
                <div style="width: 24px; height: 24px; background: ${color}; border-radius: 50%; display: flex; align-items: center; justify-content: center; color: white; font-weight: bold; font-size: 12px;">${stopIndex + 1}</div>
                <strong>${merchantName}</strong>
                ${taskTypeBadge}
              </div>
              <div style="display: flex; gap: 16px; font-size: 12px; color: #666;">
                <span>📦 ${parcelCount} parcels</span>
                <span>🕐 ${arrivalTime}</span>
              </div>
            </div>
          `);

          const marker = new mapboxgl.Marker({ element: el })
            .setLngLat([offsetLng, offsetLat])
            .setPopup(popup)
            .addTo(map.current!);
          markersRef.current.push(marker);
          bounds.extend([stop.location.lng, stop.location.lat]);
        });
      });

    // Add unassigned task markers (red)
    if (currentSimulation.result?.unassignedTaskUids && currentSimulation.result.unassignedTaskUids.length > 0) {
      currentSimulation.result.unassignedTaskUids.forEach((taskUid) => {
        const task = tasks.find(t => t.task_uid === taskUid);
        if (!task) return;

        const el = document.createElement('div');
        el.innerHTML = `
          <div style="
            width: 28px; height: 28px;
            background: #EF4444;
            border: 3px solid white;
            border-radius: 50%;
            display: flex; align-items: center; justify-content: center;
            color: white; font-weight: bold; font-size: 14px;
            box-shadow: 0 2px 6px rgba(0,0,0,0.3);
            cursor: pointer;
          ">!</div>
        `;

        const popup = new mapboxgl.Popup({ offset: 25, closeButton: false }).setHTML(`
          <div style="padding: 12px; min-width: 180px;">
            <div style="display: flex; align-items: center; gap: 8px; margin-bottom: 8px;">
              <div style="width: 24px; height: 24px; background: #EF4444; border-radius: 50%; display: flex; align-items: center; justify-content: center; color: white; font-weight: bold; font-size: 14px;">!</div>
              <strong style="color: #EF4444;">Unassigned</strong>
            </div>
            <div style="font-weight: 500; margin-bottom: 4px;">${task.merchant_name || 'Unknown'}</div>
            <div style="display: flex; gap: 16px; font-size: 12px; color: #666;">
              <span>📦 ${task.parcel_count || 0} parcels</span>
            </div>
          </div>
        `);

        const marker = new mapboxgl.Marker({ element: el })
          .setLngLat([task.lng, task.lat])
          .setPopup(popup)
          .addTo(map.current!);
        markersRef.current.push(marker);
        bounds.extend([task.lng, task.lat]);
      });
    }

    // Add excluded dedicated driver merchant markers (orange with truck icon)
    if (currentSimulation.result?.excludedDedicatedTasks && currentSimulation.result.excludedDedicatedTasks.length > 0) {
      currentSimulation.result.excludedDedicatedTasks.forEach((task) => {
        const parcelCount = task.parcel_count || 1;
        // Size based on parcel count (similar to task markers)
        const markerSize = Math.min(50, Math.max(32, 32 + Math.log(parcelCount) * 4));
        
        const el = document.createElement('div');
        el.innerHTML = `
          <div style="
            width: ${markerSize}px; height: ${markerSize}px;
            background: linear-gradient(135deg, #F97316 0%, #EA580C 100%);
            border: 3px solid #FED7AA;
            border-radius: 50%;
            display: flex; align-items: center; justify-content: center;
            flex-direction: column;
            color: white; font-weight: bold; font-size: ${Math.max(10, markerSize * 0.28)}px;
            box-shadow: 0 3px 8px rgba(249, 115, 22, 0.5);
            cursor: pointer;
            position: relative;
          ">
            <span style="font-size: ${Math.max(12, markerSize * 0.35)}px;">🚛</span>
            <span style="font-size: ${Math.max(8, markerSize * 0.22)}px; margin-top: -2px;">${parcelCount}</span>
            <div style="
              position: absolute;
              bottom: -8px;
              left: 50%;
              transform: translateX(-50%);
              background: #7C3AED;
              color: white;
              font-size: 8px;
              padding: 2px 6px;
              border-radius: 4px;
              white-space: nowrap;
              font-weight: bold;
            ">DEDICATED</div>
          </div>
        `;

        const popup = new mapboxgl.Popup({ offset: markerSize / 2 + 10, closeButton: false }).setHTML(`
          <div style="padding: 12px; min-width: 200px;">
            <div style="display: flex; align-items: center; gap: 8px; margin-bottom: 8px;">
              <div style="width: 28px; height: 28px; background: #F97316; border-radius: 50%; display: flex; align-items: center; justify-content: center; font-size: 16px;">🚛</div>
              <div>
                <strong style="color: #F97316;">Dedicated Driver Merchant</strong>
                <div style="font-size: 10px; color: #666;">Not included in routing</div>
              </div>
            </div>
            <div style="font-weight: 600; margin-bottom: 4px; font-size: 14px;">${task.merchant_name}</div>
            <div style="display: flex; gap: 16px; font-size: 12px; color: #666;">
              <span>📦 <strong>${parcelCount}</strong> parcels</span>
            </div>
            <div style="margin-top: 8px; padding: 6px 8px; background: #FFF7ED; border-radius: 4px; font-size: 11px; color: #C2410C;">
              <strong>Note:</strong> This merchant requires a dedicated driver and was excluded from the simulation routes.
            </div>
          </div>
        `);

        const marker = new mapboxgl.Marker({ element: el })
          .setLngLat([task.lng, task.lat])
          .setPopup(popup)
          .addTo(map.current!);
        markersRef.current.push(marker);
        bounds.extend([task.lng, task.lat]);
      });
    }

    map.current.fitBounds(bounds, { padding: 50 });
  }, [mapLoaded, currentSimulation, tasks, config.hubStart]);

  // Run simulation
  const handleRunSimulation = async () => {
    if (tasks.length === 0) {
      setError('No tasks available for simulation');
      return;
    }

    setIsRunning(true);
    setError(null);
    setCurrentSimulation(null);
    // Clear map routes before running new simulation
    clearMapRoutes();

    try {
      const simulationName = `Sim ${new Date().toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', timeZone: 'Asia/Baghdad' })}`;
      // Pass the filtered task UIDs so simulation runs only on displayed tasks
      const taskUids = tasks.map(t => t.task_uid);
      const { simulation } = await runSimulation({
        name: simulationName,
        date: selectedDate,
        taskUids,
        config,
      });
      setCurrentSimulation(simulation);
      loadHistory(); // Refresh history after running simulation
    } catch (err) {
      console.error('Simulation failed:', err);
      setError(err instanceof Error ? err.message : 'Simulation failed');
    } finally {
      setIsRunning(false);
    }
  };

  // Delete simulation
  const handleDeleteSimulation = async (id: string) => {
    try {
      await deleteSimulation(id);
      setHistory(prev => prev.filter(s => s._id !== id));
      if (currentSimulation?._id === id) setCurrentSimulation(null);
    } catch (err) {
      console.error('Failed to delete simulation:', err);
    }
  };

  // Clear all history
  const handleClearAllHistory = async () => {
    if (!confirm('Are you sure you want to delete all simulation history?')) return;
    try {
      await deleteAllSimulations();
      setHistory([]);
      setCurrentSimulation(null);
    } catch (err) {
      console.error('Failed to clear history:', err);
    }
  };

  // Clear map routes and markers (keep only task markers)
  const clearMapRoutes = useCallback(() => {
    if (!map.current) return;
    // Remove route layers
    const existingLayers = map.current.getStyle()?.layers || [];
    existingLayers.forEach(layer => {
      if (layer.id.startsWith('route-')) {
        map.current?.removeLayer(layer.id);
      }
    });
    // Remove route sources
    const existingSources = Object.keys(map.current.getStyle()?.sources || {});
    existingSources.forEach(source => {
      if (source.startsWith('route-')) {
        map.current?.removeSource(source);
      }
    });
    // Remove all markers
    markersRef.current.forEach(marker => marker.remove());
    markersRef.current = [];
  }, []);

  // Load config from a simulation
  const loadConfigFromSimulation = (sim: SimulationType) => {
    if (sim.config) {
      setConfig(sim.config);
    }
  };

  // Duration display
  const durationDisplay = useMemo(() => {
    const startHour = parseInt(config.startTime.split(':')[0]);
    const endHour = parseInt(config.endTime.split(':')[0]);
    const startMins = startHour * 60 + parseInt(config.startTime.split(':')[1]);
    let endMins = endHour * 60 + parseInt(config.endTime.split(':')[1]);
    if (endHour >= 0 && endHour <= 3 && startHour > 3) endMins += 1440;
    const diff = endMins - startMins;
    return `${Math.floor(diff / 60)}h ${diff % 60 > 0 ? `${diff % 60}m` : ''}`;
  }, [config.startTime, config.endTime]);

  // Section header component
  const SectionHeader = ({ title, icon: Icon, section, badge }: { title: string; icon: React.ElementType; section: keyof typeof expandedSections; badge?: string | number }) => (
    <button
      onClick={() => toggleSection(section)}
      className="w-full flex items-center justify-between p-3 hover:bg-gray-50 transition-colors"
    >
      <div className="flex items-center gap-2">
        <Icon className="h-4 w-4 text-blue-600" />
        <span className="text-sm font-medium text-gray-900">{title}</span>
        {badge !== undefined && (
          <span className="px-1.5 py-0.5 text-xs font-medium bg-blue-100 text-blue-700 rounded">
            {badge}
          </span>
        )}
      </div>
      {expandedSections[section] ? (
        <ChevronUp className="h-4 w-4 text-gray-400" />
      ) : (
        <ChevronDown className="h-4 w-4 text-gray-400" />
      )}
    </button>
  );

  return (
    <div className="h-screen flex flex-col">
      {/* Main Content - sidebar + map */}
      <div className="flex-1 flex overflow-hidden min-h-0">
        {/* Sidebar */}
        <div className="w-80 bg-white border-r border-gray-200 flex flex-col overflow-hidden">
        {/* Header */}
        <div className="p-4 border-b border-gray-200">
          <h1 className="text-lg font-bold text-gray-900">Route Simulation</h1>
          <p className="text-xs text-gray-500">Test Verso configurations</p>
        </div>

        {/* Scrollable content */}
        <div className="flex-1 overflow-y-auto">
          {/* Date & Zone */}
          <div className="border-b border-gray-100">
            <SectionHeader title="Date & Zone" icon={Calendar} section="dateZone" badge={tasks.length} />
            {expandedSections.dateZone && (
              <div className="px-3 pb-3 space-y-3">
                <input
                  type="date"
                  value={selectedDate}
                  onChange={(e) => setSelectedDate(e.target.value)}
                  className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500"
                />
                {/* Zone Filter Dropdown - like Tasks page */}
                <div className="relative" ref={zoneDropdownRef}>
                  <div 
                    className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded bg-white cursor-pointer flex items-center gap-1 flex-wrap min-h-[34px]"
                    onClick={() => setZoneDropdownOpen(!zoneDropdownOpen)}
                  >
                    {zoneFilter.length === 0 ? (
                      <span className="text-gray-500">All Zones</span>
                    ) : (
                      zoneFilter.map((zone) => (
                        <span 
                          key={zone} 
                          className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-blue-100 text-blue-800 text-xs rounded-full"
                        >
                          {zone === 'no_zone' ? 'No Zone' : zone}
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setZoneFilter(prev => prev.filter(z => z !== zone));
                            }}
                            className="hover:text-blue-600"
                          >
                            ×
                          </button>
                        </span>
                      ))
                    )}
                  </div>
                  {zoneDropdownOpen && (
                    <div className="absolute z-50 mt-1 w-full bg-white border border-gray-300 rounded shadow-lg max-h-48 overflow-auto">
                      <label className="flex items-center gap-2 px-2 py-1.5 hover:bg-gray-50 cursor-pointer border-b border-gray-200">
                        <input
                          type="checkbox"
                          checked={zoneFilter.includes('no_zone')}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setZoneFilter(prev => [...prev, 'no_zone']);
                            } else {
                              setZoneFilter(prev => prev.filter(z => z !== 'no_zone'));
                            }
                          }}
                          className="h-3 w-3 rounded border-gray-300 text-blue-600"
                        />
                        <span className="text-xs text-red-600">No Zone</span>
                      </label>
                      {availableZones.map((zone) => (
                        <label key={zone} className="flex items-center gap-2 px-2 py-1.5 hover:bg-gray-50 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={zoneFilter.includes(zone)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setZoneFilter(prev => [...prev, zone]);
                              } else {
                                setZoneFilter(prev => prev.filter(z => z !== zone));
                              }
                            }}
                            className="h-3 w-3 rounded border-gray-300 text-blue-600"
                          />
                          <span className="text-xs">{zone}</span>
                        </label>
                      ))}
                      {zoneFilter.length > 0 && (
                        <button
                          onClick={() => setZoneFilter([])}
                          className="w-full px-2 py-1.5 text-xs text-blue-600 hover:bg-blue-50 border-t border-gray-200"
                        >
                          Clear all
                        </button>
                      )}
                    </div>
                  )}
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-500">Tasks:</span>
                  <span className="font-medium">
                    {isLoadingTasks ? <Loader2 className="h-4 w-4 animate-spin" /> : tasks.length}
                  </span>
                </div>
                {!isLoadingTasks && tasks.length > 0 && (
                  <div className="flex items-center gap-2 text-xs mt-1">
                    <span className="px-1.5 py-0.5 bg-blue-100 text-blue-700 rounded font-medium">
                      {pickupCount} Pickups
                    </span>
                    {rtoCount > 0 && (
                      <span className="px-1.5 py-0.5 bg-red-100 text-red-700 rounded font-medium">
                        {rtoCount} RTOs
                      </span>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Vehicle Config */}
          <div className="border-b border-gray-100">
            <SectionHeader title="Vehicle" icon={Truck} section="vehicle" />
            {expandedSections.vehicle && (
              <div className="px-3 pb-3 space-y-3">
                <div className="flex gap-1">
                  {['auto', 'manual'].map(mode => (
                    <button
                      key={mode}
                      onClick={() => setConfig(prev => ({ ...prev, vehicleMode: mode as 'auto' | 'manual' }))}
                      className={`flex-1 px-2 py-1 text-xs font-medium rounded ${
                        config.vehicleMode === mode
                          ? 'bg-blue-600 text-white'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      {mode === 'auto' ? 'Auto' : 'Manual'}
                    </button>
                  ))}
                </div>
                {config.vehicleMode === 'manual' && (
                  <div>
                    <input
                      type="range" min="1" max="50" value={config.vehicleCount || 10}
                      onChange={(e) => setConfig(prev => ({ ...prev, vehicleCount: parseInt(e.target.value) }))}
                      className="w-full h-1"
                    />
                    <div className="text-xs text-center text-blue-600 font-medium">{config.vehicleCount} vehicles</div>
                  </div>
                )}
                <div>
                  <label className="text-xs text-gray-500">Capacity</label>
                  <input
                    type="number" min="50" max="1000" value={config.vehicleCapacity}
                    onChange={(e) => setConfig(prev => ({ ...prev, vehicleCapacity: parseInt(e.target.value) || 300 }))}
                    className="w-full px-2 py-1 text-sm border border-gray-300 rounded"
                  />
                </div>
              </div>
            )}
          </div>

          {/* Time Config */}
          <div className="border-b border-gray-100">
            <SectionHeader title="Time" icon={Clock} section="time" badge={durationDisplay} />
            {expandedSections.time && (
              <div className="px-3 pb-3 space-y-3">
                {/* Time display */}
                <div className="flex justify-between items-center">
                  <div className="text-center">
                    <div className="text-lg font-bold text-blue-600">{config.startTime}</div>
                    <div className="text-xs text-gray-500">Start</div>
                  </div>
                  <div className="flex-1 flex items-center justify-center">
                    <div className="h-0.5 w-8 bg-blue-200"></div>
                    <Clock className="h-4 w-4 text-blue-400 mx-1" />
                    <div className="h-0.5 w-8 bg-blue-200"></div>
                  </div>
                  <div className="text-center">
                    <div className="text-lg font-bold text-blue-600">{config.endTime}</div>
                    <div className="text-xs text-gray-500">End</div>
                  </div>
                </div>
                
                {/* Dual range slider - Range: 6am (360) to 3am next day (1620) */}
                {(() => {
                  const SLIDER_MIN = 360;  // 6am in minutes
                  const SLIDER_MAX = 1620; // 3am next day in minutes
                  const SLIDER_RANGE = SLIDER_MAX - SLIDER_MIN;
                  
                  const parseTimeToMins = (time: string): number => {
                    const hours = parseInt(time.split(':')[0]);
                    const mins = parseInt(time.split(':')[1]);
                    const totalMins = hours * 60 + mins;
                    if (hours >= 0 && hours <= 3) return totalMins + 1440;
                    return totalMins;
                  };
                  
                  const minsToTime = (mins: number): string => {
                    let adjustedMins = mins;
                    if (mins >= 1440) adjustedMins = mins - 1440;
                    const hours = Math.floor(adjustedMins / 60);
                    const minutes = adjustedMins % 60;
                    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
                  };
                  
                  const startMins = parseTimeToMins(config.startTime);
                  const endMins = parseTimeToMins(config.endTime);
                  const startPercent = ((startMins - SLIDER_MIN) / SLIDER_RANGE) * 100;
                  const endPercent = ((endMins - SLIDER_MIN) / SLIDER_RANGE) * 100;
                  
                  return (
                    <div className="relative h-8 flex items-center">
                      <div className="absolute w-full h-2 bg-gray-200 rounded-full pointer-events-none">
                        <div 
                          className="absolute w-0.5 h-3 bg-gray-400 -top-0.5"
                          style={{ left: `${((1440 - SLIDER_MIN) / SLIDER_RANGE) * 100}%` }}
                          title="Midnight"
                        />
                        <div 
                          className="absolute h-2 bg-blue-500 rounded-full"
                          style={{ left: `${Math.max(0, startPercent)}%`, right: `${Math.max(0, 100 - endPercent)}%` }}
                        />
                      </div>
                      <input
                        type="range" min={SLIDER_MIN} max={SLIDER_MAX} step="30"
                        value={Math.max(SLIDER_MIN, Math.min(SLIDER_MAX, startMins))}
                        onChange={(e) => {
                          const mins = parseInt(e.target.value);
                          if (mins < endMins - 30) setConfig(prev => ({ ...prev, startTime: minsToTime(mins) }));
                        }}
                        className="absolute w-full h-8 appearance-none bg-transparent cursor-pointer pointer-events-none [&::-webkit-slider-thumb]:pointer-events-auto [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-5 [&::-webkit-slider-thumb]:h-5 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-green-500 [&::-webkit-slider-thumb]:border-2 [&::-webkit-slider-thumb]:border-white [&::-webkit-slider-thumb]:shadow-lg [&::-webkit-slider-thumb]:cursor-grab"
                        style={{ zIndex: 5 }}
                      />
                      <input
                        type="range" min={SLIDER_MIN} max={SLIDER_MAX} step="30"
                        value={Math.max(SLIDER_MIN, Math.min(SLIDER_MAX, endMins))}
                        onChange={(e) => {
                          const mins = parseInt(e.target.value);
                          if (mins > startMins + 30) setConfig(prev => ({ ...prev, endTime: minsToTime(mins) }));
                        }}
                        className="absolute w-full h-8 appearance-none bg-transparent cursor-pointer pointer-events-none [&::-webkit-slider-thumb]:pointer-events-auto [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-5 [&::-webkit-slider-thumb]:h-5 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-red-500 [&::-webkit-slider-thumb]:border-2 [&::-webkit-slider-thumb]:border-white [&::-webkit-slider-thumb]:shadow-lg [&::-webkit-slider-thumb]:cursor-grab"
                        style={{ zIndex: 4 }}
                      />
                    </div>
                  );
                })()}
                
                <div className="text-xs text-gray-500 text-center">Drag green (start) and red (end) handles</div>
              </div>
            )}
          </div>

          {/* Optimization */}
          <div className="border-b border-gray-100">
            <SectionHeader title="Optimization" icon={Target} section="optimization" />
            {expandedSections.optimization && (
              <div className="px-3 pb-3 space-y-3">
                <div className="flex gap-1">
                  {[
                    { value: 'balanced', label: 'Balanced' },
                    { value: 'minimize_time', label: 'Fast' },
                    { value: 'minimize_vehicles', label: 'Cheap' },
                  ].map(goal => (
                    <button
                      key={goal.value}
                      onClick={() => setConfig(prev => ({ ...prev, optimizationGoal: goal.value as SimulationConfig['optimizationGoal'] }))}
                      className={`flex-1 px-2 py-1 text-xs rounded ${
                        config.optimizationGoal === goal.value
                          ? 'bg-blue-600 text-white'
                          : 'bg-gray-100 text-gray-700'
                      }`}
                    >
                      {goal.label}
                    </button>
                  ))}
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="text-xs text-gray-500">Sec/Parcel</label>
                    <input
                      type="number" min="1" max="60" value={config.serviceTimePerParcel}
                      onChange={(e) => setConfig(prev => ({ ...prev, serviceTimePerParcel: parseInt(e.target.value) || 5 }))}
                      className="w-full px-2 py-1 text-sm border border-gray-300 rounded"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-gray-500">Base Min</label>
                    <input
                      type="number" min="1" max="30" value={config.baseServiceTimeMinutes}
                      onChange={(e) => setConfig(prev => ({ ...prev, baseServiceTimeMinutes: parseInt(e.target.value) || 5 }))}
                      className="w-full px-2 py-1 text-sm border border-gray-300 rounded"
                    />
                  </div>
                </div>
                <label className="flex items-center justify-between cursor-pointer">
                  <span className="text-xs text-gray-700">Merchant Time Windows</span>
                  <input
                    type="checkbox" checked={config.useMerchantTimeWindows}
                    onChange={(e) => setConfig(prev => ({ ...prev, useMerchantTimeWindows: e.target.checked }))}
                    className="rounded text-blue-600"
                  />
                </label>
                <label className="flex items-center justify-between cursor-pointer">
                  <span className="text-xs text-gray-700">Exclude Dedicated Merchants</span>
                  <input
                    type="checkbox" checked={config.excludeDedicatedDriverMerchants}
                    onChange={(e) => setConfig(prev => ({ ...prev, excludeDedicatedDriverMerchants: e.target.checked }))}
                    className="rounded text-orange-600"
                  />
                </label>
                <label className="flex items-center justify-between cursor-pointer">
                  <span className="text-xs text-gray-700">Start from Hub</span>
                  <input
                    type="checkbox" checked={config.hubStart}
                    onChange={(e) => setConfig(prev => ({ ...prev, hubStart: e.target.checked }))}
                    className="rounded text-blue-600"
                  />
                </label>
                {/* Vehicle Speed Slider */}
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs text-gray-700">Vehicle Speed</span>
                    <span className="text-xs font-medium text-blue-600">{config.speedFactor.toFixed(1)}x</span>
                  </div>
                  <input
                    type="range"
                    min="0.5"
                    max="1.5"
                    step="0.1"
                    value={config.speedFactor}
                    onChange={(e) => setConfig(prev => ({ ...prev, speedFactor: Number(e.target.value) }))}
                    className="w-full h-1"
                  />
                  <div className="flex justify-between text-[10px] text-gray-500 mt-1">
                    <span>0.5x Slow</span>
                    <span>1.0x Normal</span>
                    <span>1.5x Fast</span>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Cost */}
          <div className="border-b border-gray-100">
            <SectionHeader title="Cost" icon={DollarSign} section="cost" />
            {expandedSections.cost && (
              <div className="px-3 pb-3">
                <div className="grid grid-cols-3 gap-2">
                  <div>
                    <label className="text-xs text-gray-500">$/Hour</label>
                    <input
                      type="number" min="0" step="0.5" value={config.costPerHour}
                      onChange={(e) => setConfig(prev => ({ ...prev, costPerHour: parseFloat(e.target.value) || 0 }))}
                      className="w-full px-2 py-1 text-sm border border-gray-300 rounded"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-gray-500">$/KM</label>
                    <input
                      type="number" min="0" step="0.01" value={config.costPerKm}
                      onChange={(e) => setConfig(prev => ({ ...prev, costPerKm: parseFloat(e.target.value) || 0 }))}
                      className="w-full px-2 py-1 text-sm border border-gray-300 rounded"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-gray-500">Fixed</label>
                    <input
                      type="number" min="0" step="1" value={config.fixedVehicleCost}
                      onChange={(e) => setConfig(prev => ({ ...prev, fixedVehicleCost: parseFloat(e.target.value) || 0 }))}
                      className="w-full px-2 py-1 text-sm border border-gray-300 rounded"
                    />
                  </div>
                </div>
              </div>
            )}
          </div>

        </div>

        {/* Run Button & Results */}
        <div className="border-t border-gray-200 p-3 space-y-3">
          {error && (
            <div className="p-2 bg-red-50 border border-red-200 rounded flex items-center gap-2 text-xs text-red-700">
              <AlertTriangle className="h-4 w-4 flex-shrink-0" />
              <span className="truncate">{error}</span>
            </div>
          )}

          {currentSimulation?.result && (
            <div className="grid grid-cols-3 gap-2 text-center">
              <div className="p-2 bg-blue-50 rounded">
                <div className="text-lg font-bold text-blue-600">{currentSimulation.result.totalRoutes}</div>
                <div className="text-xs text-gray-500">Routes</div>
              </div>
              <div className="p-2 bg-green-50 rounded">
                <div className="text-lg font-bold text-green-600">{currentSimulation.result.totalDistanceKm.toFixed(0)}</div>
                <div className="text-xs text-gray-500">KM</div>
              </div>
              <div className={`p-2 rounded ${currentSimulation.result.unassignedCount > 0 ? 'bg-red-50' : 'bg-gray-50'}`}>
                <div className={`text-lg font-bold ${currentSimulation.result.unassignedCount > 0 ? 'text-red-600' : 'text-gray-600'}`}>
                  {currentSimulation.result.unassignedCount}
                </div>
                <div className="text-xs text-gray-500">Unassigned</div>
              </div>
            </div>
          )}

          {can('simulation.run') && (
            <button
              onClick={handleRunSimulation}
              disabled={isRunning || tasks.length === 0}
              className="w-full flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-semibold text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
            >
              {isRunning ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Running...
                </>
              ) : (
                <>
                  <Play className="h-4 w-4" />
                  Run ({tasks.length} tasks)
                </>
              )}
            </button>
          )}
        </div>
        </div>

        {/* Map */}
        <div className="flex-1 h-full relative">
          <div ref={mapContainer} className="absolute inset-0 w-full h-full" />
          
          {/* Map overlay - Results summary with cost breakdown */}
          {currentSimulation?.result && currentSimulation?.versoResponse && (() => {
            const routes = currentSimulation.versoResponse.routes || [];
            const totalServiceSec = routes.reduce((sum, r) => sum + (r.totalServiceSeconds || 0), 0);
            // Verso's totalDurationSeconds is actually the DRIVING time (not including service)
            const totalDrivingSec = routes.reduce((sum, r) => sum + (r.totalDurationSeconds || 0), 0);
            // Total time = driving + service
            const totalTimeSec = totalDrivingSec + totalServiceSec;
            const serviceHours = (totalServiceSec / 3600).toFixed(1);
            const drivingHours = (totalDrivingSec / 3600).toFixed(1);
            const totalHours = (totalTimeSec / 3600).toFixed(1);
            const distanceKm = currentSimulation.result.totalDistanceKm.toFixed(1);
            
            return (
              <div className="absolute top-4 left-4 bg-white rounded-lg shadow-lg p-3 text-sm">
                <div className="font-semibold text-gray-900 mb-2">Simulation Results</div>
                <div className="flex flex-wrap gap-3 text-xs">
                  <span title="Total routes">
                    🚛 <strong>{currentSimulation.result.totalRoutes}</strong>
                    <span className="text-gray-500 ml-1">routes</span>
                  </span>
                  <span title="Total stops">
                    📍 <strong>{currentSimulation.result.totalStops}</strong>
                    <span className="text-gray-500 ml-1">stops</span>
                  </span>
                  <span title="Total distance">
                    📏 <strong>{distanceKm}</strong>
                    <span className="text-gray-500 ml-1">km</span>
                  </span>
                </div>
                <div className="flex flex-wrap gap-3 text-xs mt-2 pt-2 border-t border-gray-100">
                  <span title="Service time at stops">
                    🛑 <strong>{serviceHours}h</strong>
                    <span className="text-gray-500 ml-1">service</span>
                  </span>
                  <span className="text-blue-600" title="Driving time">
                    🚗 <strong>{drivingHours}h</strong>
                    <span className="text-gray-500 ml-1">driving</span>
                  </span>
                  <span className="text-green-600" title="Total time = driving + service">
                    ⏱️ <strong>{totalHours}h</strong>
                    <span className="text-gray-500 ml-1">total</span>
                  </span>
                </div>
                <div className="flex flex-wrap gap-3 text-xs mt-2 pt-2 border-t border-gray-100">
                  <span title="Total parcels">
                    📦 <strong>{currentSimulation.result.totalParcels}</strong>
                    <span className="text-gray-500 ml-1">parcels</span>
                  </span>
                  <span className="text-blue-600" title="Pickup tasks">
                    📤 <strong>{pickupCount}</strong>
                    <span className="text-gray-500 ml-1">pickups</span>
                  </span>
                  {rtoCount > 0 && (
                    <span className="text-red-600" title="RTO delivery tasks">
                      ↩️ <strong>{rtoCount}</strong>
                      <span className="text-gray-500 ml-1">RTOs</span>
                    </span>
                  )}
                </div>
                <div className="flex flex-wrap gap-3 text-xs mt-2 pt-2 border-t border-gray-100">
                  <span className="text-green-600" title="Total cost">
                    💰 <strong>${currentSimulation.result.totalCost.toFixed(2)}</strong>
                    <span className="text-gray-500 ml-1">cost</span>
                  </span>
                  {currentSimulation.result.unassignedCount > 0 && (
                    <span className="text-orange-600" title="Unassigned tasks">
                      ⚠️ <strong>{currentSimulation.result.unassignedCount}</strong>
                      <span className="ml-1">unassigned</span>
                    </span>
                  )}
                </div>
              </div>
            );
          })()}

          {/* Right side toggle buttons - positioned to the left of map zoom controls */}
          <div className="absolute top-4 right-14 flex flex-col gap-2 z-10">
            <button
              onClick={() => { setShowRoutesPanel(!showRoutesPanel); setShowHistoryPanel(false); }}
              className={`p-2 rounded-lg shadow-lg transition-colors ${
                showRoutesPanel ? 'bg-blue-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-50'
              }`}
              title="Routes"
            >
              <Route className="h-5 w-5" />
            </button>
            <button
              onClick={() => { setShowHistoryPanel(!showHistoryPanel); setShowRoutesPanel(false); loadHistory(); }}
              className={`p-2 rounded-lg shadow-lg transition-colors ${
                showHistoryPanel ? 'bg-blue-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-50'
              }`}
              title="History"
            >
              <History className="h-5 w-5" />
            </button>
          </div>

          {/* Routes Panel - Slide in from right */}
          {showRoutesPanel && currentSimulation?.versoResponse?.routes && (
            <div className="absolute top-4 right-28 w-80 max-h-[calc(100%-2rem)] bg-white rounded-lg shadow-lg overflow-hidden flex flex-col z-10">
              <div className="p-3 border-b border-gray-200 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Route className="h-4 w-4 text-blue-600" />
                  <span className="font-semibold text-gray-900">Routes ({currentSimulation.versoResponse.routes.length})</span>
                </div>
                <button onClick={() => setShowRoutesPanel(false)} className="p-1 hover:bg-gray-100 rounded">
                  <X className="h-4 w-4 text-gray-500" />
                </button>
              </div>
              <div className="flex-1 overflow-y-auto p-2 space-y-2">
                {currentSimulation.versoResponse.routes.map((route, index) => {
                  const color = ROUTE_COLORS[index % ROUTE_COLORS.length];
                  const isSelected = selectedRouteIndex === index;
                  // Driving time from Verso (totalDurationSeconds IS driving time)
                  const drivingSeconds = route.totalDurationSeconds;
                  const serviceSeconds = route.totalServiceSeconds;
                  // Total time = driving + service
                  const totalSeconds = drivingSeconds + serviceSeconds;
                  const distanceKm = (route.totalDistanceMeters / 1000).toFixed(1);
                  // Calculate total orders, RTO count, and pickup count for this route
                  let totalOrders = 0;
                  let routeRtoCount = 0;
                  let routePickupCount = 0;
                  route.stops.forEach(stop => {
                    const taskDetails = tasks.find(t => t.task_uid === stop.taskUid);
                    totalOrders += taskDetails?.parcel_count || 0;
                    if (taskDetails?.type === 'rto') {
                      routeRtoCount++;
                    } else {
                      routePickupCount++;
                    }
                  });
                  // Calculate cost based on total time (driving + service)
                  const totalHours = totalSeconds / 3600;
                  const routeCost = (totalHours * config.costPerHour) + ((route.totalDistanceMeters / 1000) * config.costPerKm) + config.fixedVehicleCost;
                  // Helper to format seconds as hours
                  const formatHours = (seconds: number) => {
                    const hours = seconds / 3600;
                    return hours.toFixed(1) + 'h';
                  };
                  
                  return (
                    <div
                      key={index}
                      onClick={() => {
                        setSelectedRouteIndex(isSelected ? null : index);
                        // Zoom to route on map
                        if (!isSelected && route.stops.length > 0 && map.current) {
                          const bounds = new mapboxgl.LngLatBounds();
                          route.stops.forEach(stop => bounds.extend([stop.location.lng, stop.location.lat]));
                          map.current.fitBounds(bounds, { padding: 100 });
                        }
                      }}
                      className={`p-3 rounded-lg cursor-pointer transition-all ${
                        isSelected 
                          ? 'ring-2 ring-blue-400 ring-offset-1' 
                          : 'hover:bg-gray-50'
                      }`}
                      style={{ 
                        backgroundColor: isSelected ? `${color}15` : undefined,
                        borderLeft: `4px solid ${color}`
                      }}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <div 
                            className="w-6 h-6 rounded-full flex items-center justify-center text-white text-xs font-bold"
                            style={{ backgroundColor: color }}
                          >
                            {index + 1}
                          </div>
                          <span className="font-medium text-gray-900">Route {index + 1}</span>
                        </div>
                        <div className="flex items-center gap-2 text-xs">
                          <span className="text-gray-500">{route.stops.length} stops</span>
                          <span className="font-medium text-blue-600">📦 {totalOrders}</span>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 text-xs mb-2">
                        <span className="px-1.5 py-0.5 rounded bg-blue-100 text-blue-700 font-medium">{routePickupCount} Pickup</span>
                        <span className="px-1.5 py-0.5 rounded bg-red-100 text-red-700 font-medium">{routeRtoCount} RTO</span>
                      </div>
                      <div className="grid grid-cols-2 gap-1 text-xs mb-2">
                        <div className="flex items-center gap-1">
                          <span className="text-gray-500">🚗</span>
                          <span className="text-gray-600">{formatHours(drivingSeconds)} driving</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <span className="text-gray-500">🛑</span>
                          <span className="text-gray-600">{formatHours(serviceSeconds)} service</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <span className="text-gray-500">⏱️</span>
                          <span className="text-gray-600">{formatHours(totalSeconds)} total</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <span className="text-gray-500">📏</span>
                          <span className="text-gray-600">{distanceKm} km</span>
                        </div>
                      </div>
                      <div className="flex items-center justify-between text-xs pt-2 border-t border-gray-100">
                        <span className="text-gray-500">Capacity: {config.vehicleCapacity}</span>
                        <span className="font-medium text-green-600">💰 ${routeCost.toFixed(2)}</span>
                      </div>
                      {isSelected && route.stops.length > 0 && (
                        <div className="mt-3 pt-3 border-t border-gray-200">
                          <div className="text-xs font-medium text-gray-700 mb-2">Stops ({totalOrders} parcels):</div>
                          <div className="space-y-1 max-h-40 overflow-y-auto">
                            {route.stops.map((stop, stopIndex) => {
                              const taskDetails = tasks.find(t => t.task_uid === stop.taskUid);
                              const parcelCount = taskDetails?.parcel_count || 0;
                              const isRTO = taskDetails?.type === 'rto';
                              const isCash = taskDetails?.type === 'cash';
                              const stopColor = isRTO ? '#DC2626' : isCash ? '#D97706' : color;
                              const stopIcon = isRTO ? '↩' : isCash ? '$' : (stopIndex + 1).toString();
                              // Format arrival time from timestamp
                              const arrivalTime = new Date(stop.arrivalTimestamp * 1000).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', timeZone: 'Asia/Baghdad' });
                              // Format service time in minutes
                              const serviceTimeMin = Math.round(stop.serviceSeconds / 60);
                              return (
                                <div key={stopIndex} className="p-1.5 rounded bg-gray-50 border border-gray-100">
                                  <div className="flex items-center gap-2 text-xs">
                                    <span 
                                      className="w-5 h-5 rounded-full flex items-center justify-center text-white text-[10px] font-bold flex-shrink-0"
                                      style={{ backgroundColor: stopColor }}
                                    >
                                      {stopIcon}
                                    </span>
                                    <span className="text-gray-600 truncate flex-1">
                                      {taskDetails?.merchant_name || 'Unknown'}
                                    </span>
                                    <span className={`px-1 py-0.5 rounded text-[9px] font-semibold flex-shrink-0 ${isRTO ? 'bg-red-100 text-red-700' : isCash ? 'bg-amber-100 text-amber-700' : 'bg-blue-100 text-blue-700'}`}>
                                      {isRTO ? 'RTO' : isCash ? 'Cash' : 'Pickup'}
                                    </span>
                                    <span className="text-blue-600 font-medium flex-shrink-0">
                                      📦 {parcelCount}
                                    </span>
                                  </div>
                                  <div className="flex items-center gap-3 text-[10px] text-gray-500 mt-1 ml-7">
                                    <span>🕐 Arrival: {arrivalTime}</span>
                                    <span>⏱️ Service: {serviceTimeMin}m</span>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* History Panel - Slide in from right */}
          {showHistoryPanel && (
            <div className="absolute top-4 right-28 w-96 max-h-[calc(100%-2rem)] bg-white rounded-lg shadow-lg overflow-hidden flex flex-col z-10">
              <div className="p-3 border-b border-gray-200 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <History className="h-4 w-4 text-blue-600" />
                  <span className="font-semibold text-gray-900">History</span>
                </div>
                <div className="flex items-center gap-1">
                  {history.length > 0 && can('simulation.run') && (
                    <button 
                      onClick={handleClearAllHistory} 
                      className="p-1 hover:bg-red-100 rounded text-gray-400 hover:text-red-500"
                      title="Clear all history"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  )}
                  <button onClick={() => setShowHistoryPanel(false)} className="p-1 hover:bg-gray-100 rounded">
                    <X className="h-4 w-4 text-gray-500" />
                  </button>
                </div>
              </div>
              <div className="flex-1 overflow-y-auto p-2">
                {isLoadingHistory ? (
                  <div className="flex justify-center py-8">
                    <Loader2 className="h-6 w-6 animate-spin text-blue-600" />
                  </div>
                ) : history.length === 0 ? (
                  <div className="text-center py-8 text-gray-500 text-sm">No simulation history</div>
                ) : (
                  <div className="space-y-3">
                    {history.map((sim) => (
                      <div
                        key={sim._id}
                        onClick={() => {
                          // Load config from this simulation
                          loadConfigFromSimulation(sim);
                          // Clear current simulation result (don't show routes)
                          setCurrentSimulation(null);
                          // Clear map routes
                          clearMapRoutes();
                          setShowHistoryPanel(false);
                          setSelectedRouteIndex(null);
                        }}
                        className={`p-3 rounded-lg cursor-pointer transition-colors border ${
                          currentSimulation?._id === sim._id 
                            ? 'bg-blue-50 border-blue-300' 
                            : 'bg-gray-50 border-gray-200 hover:bg-gray-100'
                        }`}
                      >
                        <div className="flex justify-between items-start mb-2">
                          <div>
                            <div className="font-medium text-sm text-gray-900">{sim.name}</div>
                            <div className="text-xs text-gray-500">
                              {new Date(sim.created_at).toLocaleString('en-GB', { 
                                day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' 
                              })}
                            </div>
                          </div>
                          {can('simulation.run') && (
                            <button
                              onClick={(e) => { e.stopPropagation(); handleDeleteSimulation(sim._id); }}
                              className="p-1 text-gray-400 hover:text-red-500 flex-shrink-0"
                            >
                              <Trash2 className="h-3 w-3" />
                            </button>
                          )}
                        </div>
                        
                        {sim.result ? (
                          <>
                            {/* Results Summary */}
                            <div className="grid grid-cols-4 gap-2 text-xs mb-2 p-2 bg-white rounded border border-gray-100">
                              <div className="text-center">
                                <div className="font-bold text-blue-600">{sim.result.totalRoutes}</div>
                                <div className="text-gray-500">routes</div>
                              </div>
                              <div className="text-center">
                                <div className="font-bold text-gray-700">{sim.result.totalStops}</div>
                                <div className="text-gray-500">stops</div>
                              </div>
                              <div className="text-center">
                                <div className="font-bold text-gray-700">{sim.result.totalDistanceKm.toFixed(0)}</div>
                                <div className="text-gray-500">km</div>
                              </div>
                              <div className="text-center">
                                <div className="font-bold text-green-600">${sim.result.totalCost.toFixed(0)}</div>
                                <div className="text-gray-500">cost</div>
                              </div>
                            </div>
                            {sim.result.unassignedCount > 0 && (
                              <div className="text-xs text-red-500 mb-2">⚠️ {sim.result.unassignedCount} unassigned tasks</div>
                            )}
                          </>
                        ) : sim.status === 'failed' ? (
                          <div className="text-xs text-red-500 mb-2">❌ Failed</div>
                        ) : (
                          <div className="text-xs text-gray-600 mb-2">⏳ Running...</div>
                        )}
                        
                        {/* Config Summary */}
                        {sim.config && (
                          <div className="text-[10px] text-gray-500 space-y-1 pt-2 border-t border-gray-100">
                            <div className="flex flex-wrap gap-x-3 gap-y-1">
                              <span>🚛 {sim.config.vehicleMode === 'manual' ? `${sim.config.vehicleCount} vehicles` : 'Auto'}</span>
                              <span>📦 Cap: {sim.config.vehicleCapacity}</span>
                              <span>⏰ {sim.config.startTime}-{sim.config.endTime}</span>
                              <span>🎯 {sim.config.optimizationGoal}</span>
                            </div>
                            <div className="flex flex-wrap gap-x-3 gap-y-1">
                              <span>🚗 Speed: {sim.config.speedFactor}x</span>
                              <span>💰 ${sim.config.costPerHour}/hr</span>
                              {sim.config.useMerchantTimeWindows && <span className="text-purple-600">⏰ Time Windows</span>}
                              {sim.config.hubStart && <span className="text-orange-600">🏠 Hub Start</span>}
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
